import React, { useMemo, useState } from 'react';
import {
  Briefcase, Buildings, CalendarBlank, Car, Cloud, Door, Drop, Fan, Flame, Gauge, Hash, Image, Leaf, Lightning, Link, MapPin,
  NotePencil, PencilSimple, Ruler, Signpost, Tag, Thermometer, Tree, X, type Icon,
} from '@phosphor-icons/react';
import {
  Auswahl, FeldRaster, FlipCard, ImmoButton, InnenKarte, KartenTitel, SegmentedControl, Stepper, TextFeld, ToastPill, useToastPill,
} from '../../../../../components/immobilien/ui';
import { supabase } from '../../../../../lib/supabase/client';
import { eur, qm } from '../../../../../lib/immobilien/format';
import { deDatum } from '../../../../../lib/immobilien/objektDetail';
import { meldeObjektGeaendert } from '../../../../../lib/immobilien/useObjektKontext';
import {
  ENERGIEKLASSEN, HEIZUNGSARTEN, OBJEKTTYPEN, STELLPLATZ_TYPEN, entwurfAusObjekt, entwurfOffen, entwurfZuUpdate, type StammEntwurf,
} from '../../../../../lib/immobilien/stammdaten';
import { useObjektSeite } from '../ObjektSeitenKontext';
import { sichereUrl } from '../../../../../lib/immobilien/url';

/** Farben der Energieeffizienzklassen A+ … H (Prototyp). */
const KLASSEN_FARBE = ['#1f9a55', '#3aa857', '#7cbf3f', '#c6cf2f', '#f2c12e', '#f29a2e', '#ec6a2c', '#e0442f', '#c8233a'];
const VA_LANG: Record<string, string> = { MV: 'Mietverwaltung', WEG: 'WEG-Verwaltung', SEV: 'SEV-Verwaltung', GV: 'Gewerbeverwaltung' };
const HEIZ_ICON: Record<string, Icon> = { Gas: Flame, 'Öl': Drop, 'Fernwärme': Thermometer, 'Wärmepumpe': Fan, Pellet: Tree, Elektro: Lightning, Solar: Leaf, Sonstige: Gauge };

function Energieskala({ klasse, onWahl }: { klasse: string; onWahl?: (k: string) => void }) {
  const ci = ENERGIEKLASSEN.indexOf(klasse as typeof ENERGIEKLASSEN[number]);
  return (
    <div className="flex items-end" style={{ gap: 4 }} role={onWahl ? 'radiogroup' : undefined} aria-label="Energieeffizienzklasse">
      {ENERGIEKLASSEN.map((l, i) => {
        const an = i === ci;
        const Element = onWahl ? 'button' : 'div';
        return (
          <Element key={l} type={onWahl ? 'button' : undefined} role={onWahl ? 'radio' : undefined} aria-checked={onWahl ? an : undefined}
            onClick={onWahl ? () => onWahl(an ? '' : l) : undefined}
            className={`flex items-center justify-center ${onWahl ? 'cursor-pointer' : ''}`}
            style={{ flex: 1, height: an ? 44 : 30, borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 800, color: '#fff', background: KLASSEN_FARBE[i], opacity: ci === -1 ? 0.55 : an ? 1 : 0.35,
              boxShadow: an ? `0 0 0 3px var(--bg), 0 0 0 5px ${KLASSEN_FARBE[i]}` : 'none', transition: 'all .2s' }}>
            {l}
          </Element>
        );
      })}
    </div>
  );
}

function Abschnitt({ icon: IconKomp, titel, children }: { icon: Icon; titel: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col" style={{ gap: 12 }}>
      <span className="flex items-center" style={{ gap: 8, fontSize: 15, fontWeight: 800 }}>
        <span className="flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: 10, background: 'var(--acc-soft)' }}><IconKomp weight="fill" size={15} color="var(--acc)" /></span>
        {titel}
      </span>
      {children}
    </section>
  );
}

function AuswahlFeld({ label, wert, optionen, onChange, leer = 'Bitte wählen' }: { label: string; wert: string; optionen: readonly string[]; onChange: (v: string) => void; leer?: string }) {
  const id = `stamm-${label.replace(/\W+/g, '-').toLowerCase()}`;
  const mitAltwert = wert && !optionen.includes(wert) ? [wert, ...optionen] : optionen;
  return (
    <div className="flex flex-col min-w-0" style={{ gap: 6 }}>
      <label htmlFor={id} style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>{label}</label>
      <select id={id} value={wert} onChange={e => onChange(e.target.value)}
        style={{ height: 50, borderRadius: 15, padding: '0 14px', border: 'none', background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>
        <option value="">{leer}</option>
        {mitAltwert.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

/** Panel „Technik & Stammdaten“ mit Drehkarte „Bearbeiten“ (alle Objektfelder außer Eigentümer/Zuständigkeit). */
export function StammPanel() {
  const { daten, kennzahlen: k, verwaltungsart, adresse, neuLaden, panelBearbeiten } = useObjektSeite();
  const o = daten.objekt;
  const [gedreht, setGedreht] = useState(panelBearbeiten);
  const [entwurf, setEntwurf] = useState<StammEntwurf>(() => entwurfAusObjekt(o as unknown as Record<string, unknown>));
  const [speichert, setSpeichert] = useState(false);
  const [toast, zeige] = useToastPill();
  const offen = entwurfOffen(entwurf);
  const setze = <K extends keyof StammEntwurf>(feld: K) => (wert: StammEntwurf[K]) => setEntwurf(e => ({ ...e, [feld]: wert }));

  const umschalten = () => {
    if (!gedreht) setEntwurf(entwurfAusObjekt(o as unknown as Record<string, unknown>));
    setGedreht(g => !g);
  };

  const speichere = async () => {
    if (offen.length) return;
    setSpeichert(true);
    const { error } = await supabase.from('objekte').update(entwurfZuUpdate(entwurf)).eq('id', o.id);
    setSpeichert(false);
    if (error) { zeige(`Nicht gespeichert: ${error.message}`, true); return; }
    zeige('Stammdaten gespeichert');
    setGedreht(false);
    neuLaden('objekt');
    meldeObjektGeaendert(o.id);
  };

  const oneDrive = sichereUrl(o.onedrive_link);
  const fotoOk = sichereUrl(o.bild_url) !== null;
  const baujahr = Number(o.baujahr);
  const alter = Number.isFinite(baujahr) && baujahr > 1000 ? new Date().getFullYear() - baujahr : null;
  const parken = o.stellplatz_typ && o.stellplatz_typ !== 'Keine' ? `${o.stellplatz_anzahl || 1}× ${o.stellplatz_typ}` : 'Keine';
  const HeizIcon = HEIZ_ICON[o.heizungsart ?? ''] ?? Flame;
  const stammZeilen = useMemo(() => ([
    ['Bezeichnung', o.bezeichnung], ['Objektnummer', o.objektnummer], ['Objekttyp', o.objekttyp],
    ['Verwaltungsart', verwaltungsart ? VA_LANG[verwaltungsart] : null], ['Adresse', adresse], ['Etage', o.etage], ['Status', o.status],
    ['Wohneinheiten (Stammdaten)', o.wohneinheiten !== null && o.wohneinheiten !== undefined ? String(o.wohneinheiten) : null],
    ['Soll-Miete (Stammdaten)', o.miete_soll ? eur(Number(o.miete_soll)) : null], ['Letzte Prüfung', o.letzte_pruefung ? deDatum(o.letzte_pruefung) : null],
    ['Ausstattung', o.ausstattung], ['Energieausweis', o.energieausweis],
  ] as const), [o, verwaltungsart, adresse]);

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
        <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>{gedreht ? 'Stammdaten bearbeiten' : 'Technik & Stammdaten'}</span>
        <ImmoButton art={gedreht ? 'fuellung' : 'akzent'} groesse={40} icon={gedreht ? X : PencilSimple} onClick={umschalten} aria-expanded={gedreht}>
          {gedreht ? 'Abbrechen' : 'Bearbeiten'}
        </ImmoButton>
      </div>
      <FlipCard
        gedreht={gedreht}
        vorne={
          <div className="flex flex-col" style={{ gap: 12 }}>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 10 }}>
              {([
                ['Baujahr', o.baujahr ? String(o.baujahr) : '–', alter !== null ? `${alter} Jahre alt` : 'nicht erfasst', CalendarBlank],
                ['Wohnfläche', k.flaeche > 0 ? qm(Math.round(k.flaeche)) : '–', k.einheiten && k.flaeche > 0 ? `${qm(Math.round(k.flaeche / k.einheiten))} je Einheit` : '–', Ruler],
                ['Einheiten', String(k.einheiten), o.objekttyp || '–', Door],
                ['Effizienz', o.energieklasse || '–', o.energieausweis || 'Kein Ausweis erfasst', Leaf],
              ] as const).map(([l, v, sub, IconKomp]) => (
                <div key={l} className="immo-innen relative overflow-hidden flex flex-col" style={{ borderRadius: 20, padding: '14px 16px', gap: 4 }}>
                  <IconKomp weight="fill" size={64} color="var(--acc)" className="absolute" style={{ right: -8, bottom: -12, opacity: 0.12 }} aria-hidden />
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink3)' }}>{l}</span>
                  <span className="immo-zahl" style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.05 }}>{v}</span>
                  <span className="truncate" style={{ fontSize: 12, color: 'var(--ink2)' }}>{sub}</span>
                </div>
              ))}
            </div>
            <InnenKarte radius={22} padding="16px 18px" gap={14}>
              <KartenTitel icon={Lightning} rechts={o.energieausweis || undefined}>Energieausweis</KartenTitel>
              <Energieskala klasse={o.energieklasse ?? ''} />
            </InnenKarte>
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 10 }}>
              {([['Heizung', o.heizungsart || 'Nicht erfasst', HeizIcon, 'var(--warn)', 'var(--warn-soft)'], ['Stellplätze', parken, Car, 'var(--info)', 'var(--info-soft)'],
                ['Verwaltung', verwaltungsart ? VA_LANG[verwaltungsart] ?? verwaltungsart : 'Nicht erfasst', Briefcase, 'var(--acc)', 'var(--acc-soft)']] as const).map(([kk, v, IconKomp, f, b]) => (
                <div key={kk} className="immo-innen flex items-center" style={{ borderRadius: 20, padding: 14, gap: 12 }}>
                  <span className="flex items-center justify-center shrink-0" style={{ width: 46, height: 46, borderRadius: 15, background: b }}><IconKomp weight="fill" size={22} color={f} /></span>
                  <div className="flex flex-col min-w-0" style={{ gap: 1 }}><span style={{ fontSize: 12, color: 'var(--ink3)' }}>{kk}</span><span className="truncate" style={{ fontSize: 15, fontWeight: 700 }}>{v}</span></div>
                </div>
              ))}
            </div>
            <InnenKarte radius={22} padding="6px 18px" gap={0} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', columnGap: 28 }}>
              {stammZeilen.map(([kk, v]) => (
                <div key={kk} className="flex justify-between min-w-0" style={{ gap: 12, padding: '12px 0', borderBottom: '1px solid var(--line)', fontSize: 14 }}>
                  <span className="whitespace-nowrap" style={{ color: 'var(--ink2)' }}>{kk}</span>
                  <span className="truncate" style={{ fontWeight: 600, textAlign: 'right' }} title={v ?? undefined}>{v || '–'}</span>
                </div>
              ))}
            </InnenKarte>
            <div className="flex flex-wrap" style={{ gap: 10 }}>
              {oneDrive ? (
                <a href={oneDrive} target="_blank" rel="noopener noreferrer" className="flex items-center" style={{ height: 40, padding: '0 16px', borderRadius: 20, gap: 8, fontSize: 13, fontWeight: 600, background: 'var(--info-soft)', color: 'var(--info)', textDecoration: 'none' }}>
                  <Cloud weight="fill" size={16} />OneDrive verknüpft
                </a>
              ) : (
                <span className="flex items-center" style={{ height: 40, padding: '0 16px', borderRadius: 20, gap: 8, fontSize: 13, fontWeight: 600, background: 'var(--fill2)', color: 'var(--ink2)' }}><Cloud size={16} />Kein OneDrive-Link</span>
              )}
              <span className="flex items-center" style={{ height: 40, padding: '0 16px', borderRadius: 20, gap: 8, fontSize: 13, fontWeight: 600, background: fotoOk ? 'var(--ok-soft)' : 'var(--fill2)', color: fotoOk ? 'var(--ok)' : 'var(--ink2)' }}>
                <Image weight={fotoOk ? 'fill' : 'regular'} size={16} />{fotoOk ? 'Objektfoto hinterlegt' : o.bild_url ? 'Bild-URL ungültig' : 'Objektfoto fehlt'}
              </span>
            </div>
          </div>
        }
        hinten={
          <form onSubmit={e => { e.preventDefault(); void speichere(); }} className="immo-innen flex flex-col" style={{ borderRadius: 26, padding: 20, gap: 22 }}>
            <Abschnitt icon={Buildings} titel="Objekt">
              <SegmentedControl voll hoehe={36} ariaLabel="Verwaltungsart" wert={entwurf.verwaltungsart || 'keine'}
                segmente={[{ key: 'MV', label: 'Miete' }, { key: 'WEG', label: 'WEG' }, { key: 'SEV', label: 'SEV' }, { key: 'GV', label: 'Gewerbe' }, { key: 'keine', label: 'Offen' }]}
                onChange={v => setze('verwaltungsart')(v === 'keine' ? '' : v)} />
              <FeldRaster minBreite={200}>
                <TextFeld label="Bezeichnung" icon={Tag} wert={entwurf.bezeichnung} onChange={setze('bezeichnung')} placeholder="z. B. Alte Straße 17" />
                <TextFeld label="Objektnummer" icon={Hash} wert={entwurf.objektnummer} onChange={setze('objektnummer')} placeholder="0001" required />
                <AuswahlFeld label="Objekttyp" wert={entwurf.objekttyp} optionen={OBJEKTTYPEN} onChange={setze('objekttyp')} />
                <AuswahlFeld label="Status" wert={entwurf.status} optionen={['Aktiv', 'Inaktiv', 'Archiviert']} onChange={v => setze('status')(v === 'Inaktiv' || v === 'Archiviert' ? v : 'Aktiv')} leer="Aktiv" />
              </FeldRaster>
            </Abschnitt>
            <Abschnitt icon={MapPin} titel="Adresse">
              <FeldRaster minBreite={160}>
                <TextFeld label="Straße" icon={Signpost} wert={entwurf.strasse} onChange={setze('strasse')} required voll />
                <TextFeld label="Hausnummer" wert={entwurf.hausnummer} onChange={setze('hausnummer')} />
                <TextFeld label="PLZ" wert={entwurf.plz} onChange={setze('plz')} inputMode="numeric" />
                <TextFeld label="Ort" wert={entwurf.stadt} onChange={setze('stadt')} />
                <TextFeld label="Etage" wert={entwurf.etage} onChange={setze('etage')} />
              </FeldRaster>
            </Abschnitt>
            <Abschnitt icon={Ruler} titel="Gebäude">
              <FeldRaster minBreite={170}>
                <TextFeld label="Baujahr" icon={CalendarBlank} wert={entwurf.baujahr} onChange={setze('baujahr')} inputMode="numeric" placeholder="1962" />
                <TextFeld label="Wohnfläche" wert={entwurf.wohnflaeche} onChange={setze('wohnflaeche')} inputMode="decimal" suffix="m²" placeholder="350" />
                <TextFeld label="Wohneinheiten" wert={entwurf.wohneinheiten} onChange={setze('wohneinheiten')} inputMode="numeric" />
                <TextFeld label="Soll-Miete" wert={entwurf.miete_soll} onChange={setze('miete_soll')} inputMode="decimal" suffix="€" />
              </FeldRaster>
              <div className="flex items-center justify-between flex-wrap" style={{ gap: 12, padding: '10px 12px 10px 16px', borderRadius: 16, background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
                <span className="flex items-center" style={{ gap: 10, fontSize: 14, fontWeight: 700 }}><Car size={18} color="var(--ink3)" />Stellplätze</span>
                <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
                  <select aria-label="Art der Stellplätze" value={entwurf.stellplatz_typ} onChange={e => setze('stellplatz_typ')(e.target.value)}
                    style={{ height: 40, borderRadius: 12, padding: '0 10px', border: 'none', background: 'var(--fill2)', fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>
                    {STELLPLATZ_TYPEN.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  {entwurf.stellplatz_typ !== 'Keine' && <Stepper wert={entwurf.stellplatz_anzahl} onChange={setze('stellplatz_anzahl')} min={0} max={500} label="Anzahl Stellplätze" />}
                </div>
              </div>
            </Abschnitt>
            <Abschnitt icon={Flame} titel="Heizung">
              <Auswahl art="kachel" wert={entwurf.heizungsart || null} onChange={v => setze('heizungsart')(entwurf.heizungsart === v ? '' : v)}
                optionen={[...HEIZUNGSARTEN, ...(entwurf.heizungsart && !(HEIZUNGSARTEN as readonly string[]).includes(entwurf.heizungsart) ? [entwurf.heizungsart] : [])]
                  .map(h => ({ key: h, label: h, icon: HEIZ_ICON[h] ?? Flame }))} />
            </Abschnitt>
            <Abschnitt icon={Lightning} titel="Energieausweis">
              <Energieskala klasse={entwurf.energieklasse} onWahl={setze('energieklasse')} />
              <FeldRaster minBreite={220}>
                <TextFeld label="Energieausweis" icon={Gauge} wert={entwurf.energieausweis} onChange={setze('energieausweis')} placeholder="z. B. Verbrauchsausweis, 142 kWh/m²a, bis 12/2030" voll />
                <TextFeld label="Letzte Prüfung" type="date" wert={entwurf.letzte_pruefung} onChange={setze('letzte_pruefung')} />
              </FeldRaster>
            </Abschnitt>
            <Abschnitt icon={NotePencil} titel="Ausstattung & Notizen">
              <TextFeld label="Ausstattung" wert={entwurf.ausstattung} onChange={setze('ausstattung')} placeholder="z. B. Aufzug, Balkon, Keller" />
              <div className="flex flex-col" style={{ gap: 6 }}>
                <label htmlFor="stamm-notizen" style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Notizen (Stammdaten)</label>
                <textarea id="stamm-notizen" value={entwurf.notizen} onChange={e => setze('notizen')(e.target.value)} rows={3}
                  style={{ resize: 'vertical', border: 'none', outline: 'none', borderRadius: 15, padding: 14, background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)', fontSize: 15, lineHeight: 1.5, color: 'var(--ink)' }} />
              </div>
            </Abschnitt>
            <Abschnitt icon={Cloud} titel="Cloud & Medien">
              <FeldRaster minBreite={240}>
                <TextFeld label="OneDrive-Link" icon={Link} wert={entwurf.onedrive_link} onChange={setze('onedrive_link')} type="url" placeholder="https://…" />
                <TextFeld label="Bild-URL (Objektfoto)" icon={Image} wert={entwurf.bild_url} onChange={setze('bild_url')} type="url" placeholder="https://…" />
              </FeldRaster>
            </Abschnitt>
            <div className="flex items-center flex-wrap" style={{ gap: 12, paddingTop: 14, borderTop: '1px solid var(--line)' }}>
              <span aria-live="polite" className="flex-1" style={{ fontSize: 12, fontWeight: 700, color: offen.length ? 'var(--ink3)' : 'var(--ok)', minWidth: 160 }}>
                {offen.length ? `Noch offen: ${offen.join(', ')}` : 'Alles bereit'}
              </span>
              <ImmoButton art="fuellung" groesse={48} pille={false} onClick={umschalten}>Abbrechen</ImmoButton>
              <ImmoButton type="submit" art="akzent" groesse={48} pille={false} disabled={offen.length > 0 || speichert}>{speichert ? 'Speichert …' : 'Speichern'}</ImmoButton>
            </div>
          </form>
        }
      />
    </>
  );
}
